import React, { useState, useEffect } from 'react';
import {
  Drawer, Button, Form, Input, Result, App,
} from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';
import PhoneInput from 'react-phone-input-2';

import { getUser, updateUser } from 'api/auth';
import FormItem from 'components/FormItem';

const validateURL = (_, value) => {
  if (!value) return Promise.resolve();

  const urlPattern = /^(https?:\/\/)?(www\.)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/.*)?$/;

  if (urlPattern.test(value)) {
    return Promise.resolve();
  }

  return Promise.reject(new Error('Please enter a valid URL (e.g., google.com, www.google.com, https://google.com)'));
};

function ProfileForm({ open, onClose, title }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const { message } = App.useApp();

  useEffect(() => {
    if (open) {
      fetchUserData();
    }
  }, [open]);

  const fetchUserData = async () => {
    try {
      const userData = await getUser();

      form.setFieldsValue({
        emailAddress: userData.email,
        firstName: userData.first_name || '',
        lastName: userData.last_name || '',
        phone: userData.phone || '',
        role: userData.role || '',
        calendarLink: userData.calendar_link || '',
      });
    } catch (error) {
      console.error('Error fetching user data:', error);
      message.error('Failed to load user data');
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      const updateData = {
        first_name: values.firstName,
        last_name: values.lastName,
        phone: values.phone,
        role: values.role,
        calendar_link: values.calendarLink,
      };

      await updateUser(updateData);

      setStatus('success');
      setTimeout(() => {
        setStatus(null);
        onClose();
      }, 2000);

      message.success('Profile updated successfully');
    } catch (error) {
      console.error('Error submitting form:', error);

      // Handle field-specific validation errors
      if (error.response.data.errors[0]) {
        const fieldErrors = error.response.data.errors[0];
        const [field, errorMsg] = Object.entries(fieldErrors)[0];

        // Format field name nicely and show the error
        const fieldName = field.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
        message.error(`${fieldName}: ${errorMsg}`);
      } else {
        const errorMessage = 'Failed to update profile. Please try again.';
        message.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setStatus(null);
    onClose();
  };

  const renderContent = () => {
    if (status === 'success') {
      return (
        <div style={{ padding: '0 24px', textAlign: 'center' }}>
          <Result
            status="success"
            icon={<CheckCircleFilled style={{ color: '#52c41a' }} />}
            title="Settings Saved"
            subTitle="Your email settings have been updated successfully."
          />
        </div>
      );
    }

    return (
      <div style={{ padding: '0 24px' }}>
        <Form
          form={form}
          layout="vertical"
        >
          <FormItem
            name="emailAddress"
            label={<span style={{ fontWeight: 'bold' }}>Email address</span>}
            rules={[{ required: true, message: 'Please select an email address' }]}
          >
            <Input
              disabled
              style={{ backgroundColor: '#f5f5f5' }}
            />
          </FormItem>

          <FormItem
            name="firstName"
            label={<span style={{ fontWeight: 'bold' }}>First name</span>}
          >
            <Input placeholder="Enter your first name" />
          </FormItem>

          <FormItem
            name="lastName"
            label={<span style={{ fontWeight: 'bold' }}>Last name</span>}
          >
            <Input placeholder="Enter your last name" />
          </FormItem>

          <FormItem
            name="role"
            label={<span style={{ fontWeight: 'bold' }}>Role</span>}
          >
            <Input placeholder="e.g., Sales Manager" />
          </FormItem>

          <FormItem
            name="phone"
            label={<span style={{ fontWeight: 'bold' }}>Phone number</span>}
            fullWidth
          >
            <PhoneInput
              country="us"
              buttonClass="phone-country"
              inputClass="input"
              inputProps={{
                name: 'phone',
                required: true,
                autoFocus: true,
              }}
              placeholder="Enter phone number"
              autofocus
              preferredCountries={['us', 'ca']}
              enableSearch
              regions={['america', 'europe']}
              countryCodeEditable={false}
              masks={{
                pr: '(...) ...-....',
              }}
            />
          </FormItem>

          <FormItem
            name="calendarLink"
            label={<span style={{ fontWeight: 'bold' }}>Calendar link</span>}
            rules={[{ validator: validateURL }]}
          >
            <Input
              placeholder="e.g., https://calendly.com/kevinhart"
            />
          </FormItem>
        </Form>
      </div>
    );
  };

  return (
    <Drawer
      title={title}
      placement="right"
      onClose={handleClose}
      open={open}
      width={720}
      styles={{ body: { padding: '24px 0' } }}
      footer={
        status ? null : (
          <div style={{ textAlign: 'right', padding: '10px 16px' }}>
            <Button onClick={handleClose} style={{ marginRight: 8 }}>
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleSubmit}
              loading={loading}
            >
              Save changes
            </Button>
          </div>
        )
      }
    >
      {renderContent()}
    </Drawer>
  );
}

export default ProfileForm;
